// CustomerSelection.jsx - Jewellery Plans Customer Selection
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  SearchIcon,
  UserAddIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  IdentificationIcon,
  PencilIcon,
  PhoneIcon,
  MailIcon,
  LocationMarkerIcon,
  UserIcon,
} from "@heroicons/react/outline";
import Button from "../../../../../components/common/Button";
import Badge from "../../../../../components/common/Badge";
import { notifySuccess, notifyInfo } from "../../../../../utils/notification";
import UserSelection from "pages/User/components/UserSelection";

// Helper function to format address
const formatAddress = (address) => {
  if (!address || typeof address !== "object") return "";

  const parts = [];
  if (address.label) parts.push(address.label);
  if (address.line1) parts.push(address.line1);
  if (address.line2) parts.push(address.line2);
  if (address.city) parts.push(address.city);
  if (address.state) parts.push(address.state);
  if (address.pinCode) parts.push(address.pinCode);

  return parts.join(", ");
};

// Customer Info Component
const CustomerInfoDisplay = ({ customer }) => {
  if (!customer) return null;

  return (
    <div className="space-y-3">
      {/* Basic Info */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Name */}
        <div className="flex items-start gap-2">
          <UserIcon className="w-4 h-4 text-gray-400 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs text-gray-500">Name</p>
            <p className="text-sm font-medium text-gray-900">
              {customer.name || "N/A"}
            </p>
          </div>
        </div>

        {/* Phone */}
        <div className="flex items-start gap-2">
          <PhoneIcon className="w-4 h-4 text-gray-400 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs text-gray-500">Phone</p>
            <p className="text-sm font-medium text-gray-900">
              {customer.phoneNumber || "N/A"}
            </p>
          </div>
        </div>

        {/* Email */}
        {customer.email && (
          <div className="flex items-start gap-2">
            <MailIcon className="w-4 h-4 text-gray-400 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-gray-500">Email</p>
              <p className="text-sm font-medium text-gray-900">
                {customer.email}
              </p>
            </div>
          </div>
        )}

        {/* Customer ID */}
        {customer.customerId && (
          <div className="flex items-start gap-2">
            <IdentificationIcon className="w-4 h-4 text-gray-400 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-gray-500">Customer ID</p>
              <p className="text-sm font-medium text-gray-900">
                {customer.customerId}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Address */}
      {customer.address && typeof customer.address === "object" && (
        <div className="flex items-start gap-2">
          <LocationMarkerIcon className="w-4 h-4 text-gray-400 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs text-gray-500">Address</p>
            <p className="text-sm font-medium text-gray-900">
              {formatAddress(customer.address) || "No address provided"}
            </p>
          </div>
        </div>
      )}

      {/* Additional Info */}
      <div className="grid grid-cols-2 gap-3">
        {customer.accountType && (
          <div>
            <p className="text-xs text-gray-500">Account Type</p>
            <p className="text-sm font-medium text-gray-900">
              {customer.accountType}
            </p>
          </div>
        )}
        {customer.gstin && (
          <div>
            <p className="text-xs text-gray-500">GSTIN</p>
            <p className="text-sm font-medium text-gray-900">
              {customer.gstin}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const CustomerSelection = ({ data, onNext }) => {
  const dispatch = useDispatch();
  const [selectedCustomer, setSelectedCustomer] = useState(
    data?.customer || null,
  );
  const [isUserSelectionOpen, setIsUserSelectionOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState("smart"); // smart, create, edit

  // Handle customer selection from UserSelection modal
  const handleCustomerSelected = (customer) => {
    // Ensure customer object is properly formatted
    const formattedCustomer = {
      ...customer,
      // Ensure address is an object, not a string
      address: customer.address || {},
    };

    setSelectedCustomer(formattedCustomer);
    setIsUserSelectionOpen(false);

    // Show appropriate notification
    if (customer.jewelleryProfile?.kycVerified) {
      notifySuccess(`Customer ${customer.name} selected (KYC Verified)`);
    } else if (customer.jewelleryProfile?.governmentId) {
      notifyInfo(
        `Customer ${customer.name} selected (KYC Pending Verification)`,
      );
    } else {
      notifyInfo(
        `Customer ${customer.name} selected - KYC required for jewellery plans`,
      );
    }
  };

  // Open selection modal in different modes
  const openCustomerSelection = (mode = "smart") => {
    setSelectionMode(mode);
    setIsUserSelectionOpen(true);
  };

  // Handle proceed to next step
  const handleProceed = () => {
    if (selectedCustomer) {
      // Check if KYC is complete for jewellery plans
      const hasRequiredKYC =
        selectedCustomer.jewelleryProfile?.governmentId &&
        selectedCustomer.jewelleryProfile?.nominee?.name;

      if (!hasRequiredKYC) {
        notifyInfo(
          "KYC information is required for jewellery plans. Please complete KYC details.",
        );
        openCustomerSelection("edit");
        return;
      }

      onNext({
        userId: selectedCustomer._id || selectedCustomer.customerId,
        customer: selectedCustomer,
      });
    }
  };

  // Check KYC status
  const getKYCStatus = () => {
    if (!selectedCustomer) return null;

    const hasGovernmentId = selectedCustomer.jewelleryProfile?.governmentId;
    const hasNominee = selectedCustomer.jewelleryProfile?.nominee?.name;
    const isVerified = selectedCustomer.jewelleryProfile?.kycVerified;

    if (isVerified) {
      return {
        status: "verified",
        variant: "success",
        message: "KYC Verified",
      };
    } else if (hasGovernmentId && hasNominee) {
      return {
        status: "complete",
        variant: "warning",
        message: "KYC Pending Verification",
      };
    } else if (hasGovernmentId || hasNominee) {
      return {
        status: "partial",
        variant: "warning",
        message: "KYC Incomplete",
      };
    } else {
      return { status: "none", variant: "danger", message: "KYC Required" };
    }
  };

  const kycStatus = getKYCStatus();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-gray-900 mb-2">
          Select Customer
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Choose an existing customer or create a new one for the jewellery plan
        </p>

        {/* Main Selection Section */}
        {!selectedCustomer ? (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <UserAddIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Customer Selected
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Search for an existing customer or create a new one
            </p>
            <div className="flex justify-center gap-3">
              <Button
                variant="primary"
                onClick={() => openCustomerSelection("smart")}
                className="inline-flex items-center"
              >
                <SearchIcon className="h-4 w-4 mr-2" />
                Find or Create Customer
              </Button>
              <Button
                variant="secondary"
                onClick={() => openCustomerSelection("create")}
                className="inline-flex items-center"
              >
                <UserAddIcon className="h-4 w-4 mr-2" />
                Quick Create
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Selected Customer Card */}
            <div className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
              <div className="flex justify-between items-start mb-3">
                <h3 className="text-sm font-semibold text-gray-700">
                  Selected Customer
                </h3>
                <div className="flex items-center gap-2">
                  {kycStatus && (
                    <Badge variant={kycStatus.variant} size="sm">
                      {kycStatus.message}
                    </Badge>
                  )}
                  <button
                    onClick={() => {
                      setSelectionMode("edit");
                      setIsUserSelectionOpen(true);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                    title="Edit Customer"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Customer Details Display */}
              <CustomerInfoDisplay customer={selectedCustomer} />

              {/* KYC Details if available */}
              {selectedCustomer.jewelleryProfile?.governmentId && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-xs font-semibold text-gray-600 mb-2">
                    KYC Information
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">ID Type:</span>{" "}
                      <span className="font-medium">
                        {selectedCustomer.jewelleryProfile.governmentIdType ||
                          "N/A"}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">ID Number:</span>{" "}
                      <span className="font-medium">
                        ****
                        {selectedCustomer.jewelleryProfile.governmentIdLast4 ||
                          (selectedCustomer.jewelleryProfile.governmentId
                            ? selectedCustomer.jewelleryProfile.governmentId.slice(
                                -4,
                              )
                            : "N/A")}
                      </span>
                    </div>
                    {selectedCustomer.jewelleryProfile.nominee?.name && (
                      <>
                        <div>
                          <span className="text-gray-500">Nominee:</span>{" "}
                          <span className="font-medium">
                            {selectedCustomer.jewelleryProfile.nominee.name}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Relationship:</span>{" "}
                          <span className="font-medium">
                            {selectedCustomer.jewelleryProfile.nominee
                              .relationship || "N/A"}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-4 flex justify-between items-center">
                <button
                  type="button"
                  onClick={() => setSelectedCustomer(null)}
                  className="text-sm text-red-600 hover:text-red-700"
                >
                  Remove Selection
                </button>
                <button
                  type="button"
                  onClick={() => openCustomerSelection("smart")}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  Change Customer
                </button>
              </div>
            </div>

            {/* KYC Warning if incomplete */}
            {kycStatus &&
              (kycStatus.status === "none" ||
                kycStatus.status === "partial") && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex">
                    <ExclamationCircleIcon className="h-5 w-5 text-yellow-600 mt-0.5" />
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-yellow-800">
                        KYC Information Required
                      </h4>
                      <p className="mt-1 text-sm text-yellow-700">
                        Jewellery plans require complete KYC information
                        including government ID and nominee details.
                      </p>
                      <button
                        onClick={() => {
                          setSelectionMode("edit");
                          setIsUserSelectionOpen(true);
                        }}
                        className="mt-2 text-sm font-medium text-yellow-800 hover:text-yellow-900"
                      >
                        Complete KYC Now →
                      </button>
                    </div>
                  </div>
                </div>
              )}

            {/* Plan Eligibility Info */}
            {selectedCustomer.customerCategory && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <IdentificationIcon className="h-4 w-4 text-blue-600" />
                  <p className="text-sm text-blue-800">
                    Customer Category:{" "}
                    <span className="font-medium">
                      {selectedCustomer.customerCategory}
                    </span>
                    {selectedCustomer.planPermissions?.maxActivePlans && (
                      <span className="ml-2">
                        • Max Plans:{" "}
                        {selectedCustomer.planPermissions.maxActivePlans}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Proceed Button */}
      <div className="flex justify-end">
        <button
          onClick={handleProceed}
          disabled={!selectedCustomer}
          className={`px-6 py-2 rounded-md text-sm font-medium transition-colors
            ${
              selectedCustomer
                ? "bg-blue-600 text-white hover:bg-blue-700"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
        >
          {selectedCustomer
            ? kycStatus?.status === "none" || kycStatus?.status === "partial"
              ? "Complete KYC & Continue"
              : "Continue to Plan Details"
            : "Select Customer to Continue"}
        </button>
      </div>

      {/* UserSelection Modal */}
      <UserSelection
        isOpen={isUserSelectionOpen}
        onClose={() => setIsUserSelectionOpen(false)}
        onUserSelected={handleCustomerSelected}
        mode={selectionMode}
        existingUser={selectionMode === "edit" ? selectedCustomer : null}
        title={
          selectionMode === "edit"
            ? "Update Customer Details"
            : "Select Customer for Jewellery Plan"
        }
        // Configuration for jewellery plans
        showKYC={true} // Always show KYC for jewellery plans
        showAddress={true}
        showGSTIN={false} // Optional for jewellery
        showAlternatePhone={true}
        showEmail={true}
        // Required fields for jewellery plans
        requiredFields={["email", "address", "governmentId", "nominee"]}
        // Features
        allowSearch={true}
        allowCreate={true}
        allowEdit={true}
        autoFillFromSearch={true}
      />
    </div>
  );
};

export default CustomerSelection;
